/**
 * EmployeeSchedulePage.tsx
 * Orchestrator for the Employee Schedule (UX overhaul). Hosts the view switcher
 * (Matrix / Weekly Board / Timeline), the shared data + mutation hooks, the
 * detail drawer, comprehensive search, and constraint-aware reassign (mentor /
 * overbook dialogs). Thin — each view is its own component.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Alert, ToggleButton, ToggleButtonGroup, Button, IconButton,
  TextField, InputAdornment, Snackbar, LinearProgress,
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import { usePermissions } from '../../../hooks/usePermissions';
import { useEmployeeScheduleViewState } from './useEmployeeScheduleViewState';
import { useEmployeeScheduleData } from '../../../hooks/useEmployeeScheduleData';
import { useGraphPhotos } from '../../../hooks/useGraphPhotos';
import { useEmployeeAssignmentMutations, type ReassignOpts } from '../../../hooks/useEmployeeAssignmentMutations';
import { useScheduleAutoRefresh } from '../../../hooks/useScheduleAutoRefresh';
import { useSchedulePresence } from '../../../hooks/useSchedulePresence';
import { PresenceAvatars } from '../../../components/schedule/PresenceAvatars';
import type { ScheduleView } from '../../../services/scheduleEmployeeBars';
import { EmployeeMatrixView } from './EmployeeMatrixView';
import { EmployeeWeeklyBoardView } from './EmployeeWeeklyBoardView';
import { EmployeeTimelineView } from './EmployeeTimelineView';
import { EmployeeScheduleLegend } from './EmployeeScheduleLegend';
import { AssignmentDetailDrawer } from '../../../components/schedule/AssignmentDetailDrawer';
import { MentorRequiredModal } from '../../../components/schedule/MentorRequiredModal';
import { AddAssignmentDialog } from './AddAssignmentDialog';
import { matchesEmployeeSearch } from '../../../utils/employeeScheduleSearch';
import { SkillGateError, AbsenceDayConflictError, type SkillGateBlockedUser } from '../../../services/scheduleAssignments';
import { onScheduleChange, emitScheduleChange } from '../../../services/scheduleBus';
import { useScheduleHolidays } from '../../../hooks/useScheduleHolidays';
import type { EmployeeBar } from '../../../services/scheduleEmployeeBars';

function fmt(iso: string, withYear = true): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' as const } : {}), timeZone: 'UTC',
  });
}

/** "Jul 25 – Sep 18, 2026" — the year prints once when both ends share it. */
function fmtRange(startIso: string, endIso: string): string {
  const sameYear = startIso.slice(0, 4) === endIso.slice(0, 4);
  return `${fmt(startIso, !sameYear)} – ${fmt(endIso)}`;
}

export const EmployeeSchedulePage: React.FC = () => {
  const { canEditScheduling } = usePermissions();
  const readOnly = !canEditScheduling;
  const [searchParams] = useSearchParams();
  const vs = useEmployeeScheduleViewState(searchParams.get('focusWeekStart') || undefined);
  const data = useEmployeeScheduleData(vs.windowStart, vs.windowEnd);
  // P-22 (audit 2026-07-01): stat holidays in the window — weekly % divides by
  // REAL working days (Canada-Day week = 4), matching the holiday-aware backend
  // numerator. Best-effort: fetch failure -> empty set -> legacy /5 exactly.
  const { nameByDate: holidayNames } = useScheduleHolidays(vs.windowStart, vs.windowEnd);
  const holidaySet = useMemo(() => new Set(holidayNames.keys()), [holidayNames]);
  // Real Graph headshots (fall back to initials), merged onto the user list.
  const photoEmails = useMemo(() => data.users.map((u) => u.email).filter(Boolean), [data.users]);
  const photos = useGraphPhotos(photoEmails);
  const usersWithPhotos = useMemo(
    () => data.users.map((u) => ({ ...u, photoUrl: photos.get(u.email) ?? u.photoUrl })),
    [data.users, photos],
  );

  const [snack, setSnack] = useState<{ msg: string; sev: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const notify = useCallback((msg: string, sev: 'success' | 'error' | 'info' | 'warning' = 'info') => setSnack({ msg, sev }), []);
  const mut = useEmployeeAssignmentMutations(data, notify);

  // UI state
  const [collapsedTiers, setCollapsedTiers] = useState<Set<string>>(new Set());
  const [expandedUserKey, setExpandedUserKey] = useState<string | null>(searchParams.get('focusUserKey'));
  const [query, setQuery] = useState('');
  const [drawerBar, setDrawerBar] = useState<EmployeeBar | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  // reassign constraint dialogs
  // Stashed reassign attempt awaiting a mentor / assign-anyway resolution.
  // `opts` carries the flags already confirmed (W2-8). An absence-day conflict
  // (Q3B) is NOT stashed — it is a hard block with no retry path.
  const pendingReassign = useRef<{ bar: EmployeeBar; target: string; opts?: ReassignOpts } | null>(null);
  const [mentorState, setMentorState] = useState<{ taskTypeKey: string | null; blocked: SkillGateBlockedUser[] } | null>(null);

  // live-refresh + presence
  const viewKey: ScheduleView = vs.viewMode === 'board' ? 'weekly-board' : vs.viewMode === 'timeline' ? 'resource-timeline' : 'employee-schedule';
  const viewers = useSchedulePresence(viewKey);
  const { lastSyncedLabel } = useScheduleAutoRefresh({
    reload: data.reload,
    paused: !!drawerBar || mut.busy || !!mentorState,
  });

  // Cross-view live refresh: when a booking/assignment changes anywhere (the Machine
  // Schedule drag/move, Scenario Planning, or here), quietly revalidate so the
  // day-by-day breakdown + capacity reflect the corrected dates. quiet:true keeps the
  // grid mounted and patches bars in place — no remount, no full-screen gate, no flash.
  // Debounced so a flurry of drags coalesces into one refetch.
  const reloadRef = useRef(data.reload);
  useEffect(() => { reloadRef.current = data.reload; }, [data.reload]);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = onScheduleChange(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { void reloadRef.current({ quiet: true }); }, 300);
    });
    return () => { if (timer) clearTimeout(timer); off(); };
  }, []);

  const onToggleTier = useCallback((label: string) => {
    setCollapsedTiers((s) => { const n = new Set(s); n.has(label) ? n.delete(label) : n.add(label); return n; });
  }, []);

  // ── search filter ───────────────────────────────────────────────────────
  const { users, bars } = useMemo(() => {
    if (!query.trim()) return { users: usersWithPhotos, bars: data.bars };
    const barsByUser = new Map<string, EmployeeBar[]>();
    for (const b of data.bars) { const a = barsByUser.get(b.userKey) ?? []; a.push(b); barsByUser.set(b.userKey, a); }
    const users = usersWithPhotos.filter((u) => matchesEmployeeSearch(query, u, barsByUser.get(u.userKey) ?? []));
    const keep = new Set(users.map((u) => u.userKey));
    return { users, bars: data.bars.filter((b) => keep.has(b.userKey)) };
  }, [query, usersWithPhotos, data.bars]);

  // ── reassign with constraint handling ─────────────────────────────────────
  const doReassign = useCallback(async (bar: EmployeeBar, target: string, opts?: ReassignOpts) => {
    try {
      await mut.commitReassign(bar, target, opts);
    } catch (err) {
      if (err instanceof SkillGateError) {
        pendingReassign.current = { bar, target, opts };
        setMentorState({ taskTypeKey: err.taskTypeKey, blocked: err.blockedUsers });
      } else if (err instanceof AbsenceDayConflictError) {
        // Q3B: assigning onto a day off is a HARD block — show the server's
        // message verbatim; there is no Save-anyway for this code.
        notify(err.message, 'error');
      } else {
        notify(err instanceof Error ? err.message : 'Reassign failed', 'error');
      }
    }
  }, [mut, notify]);

  const onBarClick = useCallback((bar: EmployeeBar) => setDrawerBar(bar), []);
  const drawerUser = useMemo(() => usersWithPhotos.find((u) => u.userKey === drawerBar?.userKey) ?? null, [usersWithPhotos, drawerBar]);

  return (
    // background.paper (not the sage page bg) so the toolbar/legend band reads
    // as ONE surface with the white schedule grid — 2026-07-26 feedback.
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', backgroundColor: 'background.paper', borderRadius: 1, px: 1 }}>
      {/* Compact workspace toolbar (designer pass 2026-07-26): the old page-intro
          block (h6 title + description + whitespace) is folded into ONE control
          row — title · ‹ range › · Today · view switcher · search · actions —
          so the schedule matrix gets the vertical space. The page's purpose is
          clear from the nav; the description added nothing. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', py: 0.75, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 0.75 }}>
          <TodayIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ lineHeight: 1.2 }}>Employee Schedule</Typography>
        </Box>

        <IconButton size="small" aria-label="Previous window" onClick={vs.goPrev} sx={{ p: 0.25 }}><ChevronLeftIcon fontSize="small" /></IconButton>
        <Typography variant="body2" fontWeight={600} noWrap>{fmtRange(vs.windowStart, vs.windowEnd)}</Typography>
        <IconButton size="small" aria-label="Next window" onClick={vs.goNext} sx={{ p: 0.25 }}><ChevronRightIcon fontSize="small" /></IconButton>
        <Button size="small" variant="outlined" onClick={vs.goToday} sx={{ py: 0.1, px: 1, minWidth: 0, textTransform: 'none' }}>Today</Button>

        <ToggleButtonGroup
          size="small" exclusive value={vs.viewMode} onChange={(_, v) => v && vs.setViewMode(v)}
          sx={{ ml: 0.75, '& .MuiToggleButton-root': { py: 0.3, px: 1, fontSize: '0.72rem', textTransform: 'none' } }}
        >
          <ToggleButton value="matrix"><GridViewIcon sx={{ fontSize: 15, mr: 0.5 }} />Matrix</ToggleButton>
          <ToggleButton value="board"><ViewWeekIcon sx={{ fontSize: 15, mr: 0.5 }} />Board</ToggleButton>
          <ToggleButton value="timeline"><ViewTimelineIcon sx={{ fontSize: 15, mr: 0.5 }} />Timeline</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          size="small" placeholder="Search people, tasks, WO#…" value={query}
          onChange={(e) => setQuery(e.target.value)} sx={{ ml: 'auto', width: 230 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <PresenceAvatars viewers={viewers} />
        {!readOnly && <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)} sx={{ whiteSpace: 'nowrap' }}>Add Assignment</Button>}
      </Box>

      {/* Colour key — one narrow row, collapsible (preference remembered). */}
      <EmployeeScheduleLegend />

      {data.error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => data.setError(null)}>{data.error}</Alert>}
      {/* Progress bar ONLY on the first load — background/quiet refreshes (poll, bus,
          window change) update silently so the schedule never flashes (Gigi, 2026-06-22).
          The unobtrusive "syncing…" footer label is the background-refresh indicator. */}
      {data.loading && <LinearProgress sx={{ height: 2 }} />}

      {/* body */}
      <Box sx={{ flex: 1, overflow: 'hidden', mt: 0.5 }}>
        {data.loading ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading schedule…</Typography>
        ) : vs.viewMode === 'matrix' ? (
          <EmployeeMatrixView
            users={users} bars={bars} capacityByKey={data.capacityByKey} weekStarts={data.weekStarts}
            expandedUserKey={expandedUserKey} onToggleExpand={(k) => setExpandedUserKey((p) => (p === k ? null : k))}
            onBarClick={onBarClick} holidays={holidaySet}
            onExtendWeeks={vs.extendWeeks}
          />
        ) : vs.viewMode === 'board' ? (
          <EmployeeWeeklyBoardView
            users={users} bars={bars} capacityByKey={data.capacityByKey} anchorWeek={vs.anchorWeek}
            collapsedTiers={collapsedTiers} onToggleTier={onToggleTier} onBarClick={onBarClick}
            onReassign={(bar, target) => void doReassign(bar, target)} readOnly={readOnly}
            holidays={holidaySet}
          />
        ) : (
          <EmployeeTimelineView
            users={users} bars={bars} weekStarts={data.weekStarts}
            windowStart={vs.windowStart} windowEnd={vs.windowEnd} onBarClick={onBarClick}
          />
        )}
      </Box>

      {/* footer */}
      <Box sx={{ pt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
          Last synced {lastSyncedLabel} · live updates on{data.refreshing ? ' · syncing…' : ''}
        </Typography>
      </Box>

      {/* drawer */}
      <AssignmentDetailDrawer
        open={!!drawerBar} bar={drawerBar} user={drawerUser} readOnly={readOnly}
        onClose={() => setDrawerBar(null)}
        onReassign={(bar, target) => { setDrawerBar(null); void doReassign(bar, target); }}
        onDelete={(bar) => void mut.commitDelete(bar)}
      />

      {/* mentor-required (skill gate — advisory since W2-8) */}
      <MentorRequiredModal
        open={!!mentorState} taskTypeKey={mentorState?.taskTypeKey ?? null} blockedUsers={mentorState?.blocked ?? []}
        onClose={() => { setMentorState(null); pendingReassign.current = null; }}
        onAddMentor={(mentor) => {
          const p = pendingReassign.current; setMentorState(null);
          if (p) void doReassign(p.bar, p.target, { ...p.opts, mentorUserKey: mentor.AppUserKey });
        }}
        onAssignAnyway={() => {
          const p = pendingReassign.current; setMentorState(null);
          if (p) void doReassign(p.bar, p.target, { ...p.opts, confirmSkillGate: true });
        }}
      />

      {/* add assignment */}
      <AddAssignmentDialog
        open={addOpen} onClose={() => setAddOpen(false)}
        onSuccess={(overloadHint) => {
          setAddOpen(false);
          void data.reload({ quiet: true });
          emitScheduleChange({ kind: 'assignments-changed', source: 'employee-schedule' });
          // G2-7: non-blocking weekly-overload advisory at assign time.
          if (overloadHint) setSnack({ msg: overloadHint, sev: 'warning' });
        }}
        prefillUserKey={null} prefillUserName={null} prefillWeekStart={vs.anchorWeek}
      />

      <Snackbar open={!!snack} autoHideDuration={3500} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)} variant="filled">{snack.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};
